import produce from 'immer';
import React, {useMemo, useState} from 'react';
import reactFastCompare from 'react-fast-compare';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessSchemaMapperType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartSchemaMapperConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {MappableProcessType} from 'processes/ProcessConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {SchemaInfo, getSchemaFromSchemaInfo, FieldToFieldMapperData} from './FieldMappingConstants';
import SchemaMapperConfigModalBody from './SchemaMapperConfigModalBody';

// Exported for test only
export function getDisabledMessage(
  fieldToFieldMapperData: FieldToFieldMapperData,
  fromSchemaInfos: Array<SchemaInfo>,
  toSchema: Schema
): string | null {
  if (!areAllRequiredFieldsMapped(fieldToFieldMapperData, toSchema)) {
    return 'All required fields must have a configured mapping.';
  } else if (!areAllSelectedSourcesMapped(fromSchemaInfos, fieldToFieldMapperData)) {
    return 'All source schemas must be used in the mapping.';
  }
  return null;
}

function areAllRequiredFieldsMapped(
  fieldToFieldMapperData: FieldToFieldMapperData,
  toSchema: Schema
): boolean {
  return toSchema.fields
    .filter(({isRequired}) => isRequired)
    .every(({apiId}) => fieldToFieldMapperData[apiId] != null);
}

function areAllSelectedSourcesMapped(
  fromSchemas: Array<SchemaInfo>,
  fieldToFieldMapperData: FieldToFieldMapperData
): boolean {
  const uniqueFromTaskSchemaIds = _.unique(fromSchemas.map((fromTaskSchema) => fromTaskSchema.taskSchema.id));
  const configuredSourceSchemas: Array<string> = [];
  for (const fieldId of Object.keys(fieldToFieldMapperData)) {
    const fromSchemaId = fieldToFieldMapperData[fieldId].fromSchemaId;
    if (fromSchemaId != null && !configuredSourceSchemas.includes(fromSchemaId)) {
      configuredSourceSchemas.push(fromSchemaId);
    }
  }

  if (uniqueFromTaskSchemaIds.length !== _.unique(configuredSourceSchemas).length) {
    return false;
  }
  return true;
}

function areSameMappings(
  initialSchemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null,
  initialFieldToFieldMapperData: FieldToFieldMapperData,
  fieldToFieldMapperData: FieldToFieldMapperData
): boolean {
  return (
    Boolean(initialSchemaMapperConfig) &&
    reactFastCompare(initialFieldToFieldMapperData, fieldToFieldMapperData)
  );
}

function getFieldToFieldMapperData(
  toSchemaInfo: SchemaInfo,
  schemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null
): FieldToFieldMapperData {
  const toTaskOrOutputSchema = getSchemaFromSchemaInfo(toSchemaInfo);
  const fieldToFieldMapperData = toTaskOrOutputSchema.fields.reduce((acc, field: SchemaField) => {
    const schemaFieldMapperConfig = schemaMapperConfig?.schemaFieldMapperConfigs.find(
      (config) => config.toSchemaFieldId === field.apiId
    );
    acc[field.apiId] = {
      lookupConfig: schemaFieldMapperConfig?.lookupConfig ?? null,
      fromSchemaId: schemaFieldMapperConfig?.fromSchemaId ?? null,
      resultMustBeNonNullToMap: schemaFieldMapperConfig?.resultMustBeNonNullToMap ?? false,
    };
    return acc;
  }, {} as FieldToFieldMapperData);
  return fieldToFieldMapperData;
}

function generateSchemaMapperConfig(
  toSchemaInfo: SchemaInfo,
  fieldToFieldMapperData: FieldToFieldMapperData
): ProcessFlowchartSchemaMapperConfigSpec | null {
  const mappedFieldIds = Object.keys(fieldToFieldMapperData).filter(
    (fieldId) => fieldToFieldMapperData[fieldId].lookupConfig != null
  );
  if (mappedFieldIds.length === 0) {
    return null;
  }
  return {
    clientId: uuidv4(),
    toSchemaId: getSchemaFromSchemaInfo(toSchemaInfo).id,
    schemaFieldMapperConfigs: mappedFieldIds.map((fieldId) => ({
      fromSchemaId: fieldToFieldMapperData[fieldId].fromSchemaId!,
      toSchemaFieldId: fieldId,
      lookupConfig: fieldToFieldMapperData[fieldId].lookupConfig!,
      resultMustBeNonNullToMap: fieldToFieldMapperData[fieldId].resultMustBeNonNullToMap,
    })),
    mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
  };
}

export interface BaseSchemaMapperConfigModalProps {
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  fromSchemaInfos: Array<SchemaInfo>;
  toSchemaInfo: SchemaInfo;
  schemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null;
  allSchemasById: SchemaById;
  disabled?: boolean;
}

interface SchemaMapperConfigModalProps extends BaseSchemaMapperConfigModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (schemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null | undefined) => void;
}

export default function SchemaMapperConfigModal(props: SchemaMapperConfigModalProps): JSX.Element {
  assert(props.fromSchemaInfos.length > 0, 'Expected at least one mapper source');

  const [fromSchemaInfos, setFromSchemaInfos] = useState<Array<SchemaInfo>>(props.fromSchemaInfos);
  const initialFieldToFieldMapperData = useMemo(() => {
    const fieldToLookupSpec = getFieldToFieldMapperData(props.toSchemaInfo, props.schemaMapperConfig);
    if (props.fromSchemaInfos.length === 1) {
      for (const fieldId of Object.keys(fieldToLookupSpec)) {
        if (fieldToLookupSpec[fieldId].fromSchemaId == null) {
          fieldToLookupSpec[fieldId].fromSchemaId = getSchemaFromSchemaInfo(props.fromSchemaInfos[0]).id;
        }
      }
    }
    return fieldToLookupSpec;
  }, [props.toSchemaInfo, props.schemaMapperConfig, props.fromSchemaInfos]);
  const [fieldToFieldMapperData, setFieldToFieldMapperData] = useState<FieldToFieldMapperData>(
    initialFieldToFieldMapperData
  );

  const onChangeFromItemType = (itemType: MappableProcessType, idx: number): void => {
    const changingFromSchemaInfo = fromSchemaInfos[idx];
    if (itemType === changingFromSchemaInfo.itemType) {
      return;
    }
    const newFromSchemaInfos = produce(fromSchemaInfos, (draftFromSchemaInfos) => {
      draftFromSchemaInfos[idx].itemType = itemType;
    });
    const newFieldIdToLookupStepDict = produce(fieldToFieldMapperData, (draftFieldToLookupStepDict) => {
      for (const [toSchemaFieldId, {fromSchemaId}] of Object.entries(draftFieldToLookupStepDict)) {
        if (
          fromSchemaId == null ||
          (changingFromSchemaInfo.taskSchema.id !== fromSchemaId &&
            changingFromSchemaInfo.taskSchema.outputSchema?.id !== fromSchemaId)
        ) {
          continue;
        }
        draftFieldToLookupStepDict[toSchemaFieldId] = {
          lookupConfig: null,
          resultMustBeNonNullToMap: false,
          fromSchemaId:
            itemType === ModelTypeConstants.PROCESS_OUTPUT
              ? changingFromSchemaInfo.taskSchema.outputSchema!.id
              : changingFromSchemaInfo.taskSchema.id,
        };
      }
    });
    setFromSchemaInfos(newFromSchemaInfos);
    setFieldToFieldMapperData(newFieldIdToLookupStepDict);
  };

  const onChangeFieldMapperSource = (toSchemaFieldId: string, sourceTaskSchemaId: string): void => {
    const sourceSchema = getSchemaFromSchemaInfo(
      fromSchemaInfos.find((schemaInfo) => schemaInfo.taskSchema.id === sourceTaskSchemaId)!
    );
    setFieldToFieldMapperData({
      ...fieldToFieldMapperData,
      [toSchemaFieldId]: {
        lookupConfig: null,
        fromSchemaId: sourceSchema.id,
        resultMustBeNonNullToMap: false,
      },
    });
  };

  const onChangeResultMustBeNonNullToMap = (
    toSchemaFieldId: string,
    resultMustBeNonNullToMap: boolean
  ): void => {
    setFieldToFieldMapperData(
      produce(fieldToFieldMapperData, (draftFieldToFieldMapperData) => {
        draftFieldToFieldMapperData[toSchemaFieldId].resultMustBeNonNullToMap = resultMustBeNonNullToMap;
      })
    );
  };

  const removeLookupConfigForField = (fieldId: string): void => {
    setFieldToFieldMapperData(
      produce(fieldToFieldMapperData, (draftFieldToFieldMapperData) => {
        draftFieldToFieldMapperData[fieldId].lookupConfig = null;
      })
    );
  };

  const onSubmitLookupConfig = (fieldId: string, lookupConfig: LookupConfigSpec): void => {
    setFieldToFieldMapperData(
      produce(fieldToFieldMapperData, (draftFieldToFieldMapperData) => {
        draftFieldToFieldMapperData[fieldId].lookupConfig = lookupConfig;
      })
    );
  };

  const mappingsHaveChanged = !areSameMappings(
    props.schemaMapperConfig,
    initialFieldToFieldMapperData,
    fieldToFieldMapperData
  );
  const disabledMessage = getDisabledMessage(
    fieldToFieldMapperData,
    props.fromSchemaInfos,
    getSchemaFromSchemaInfo(props.toSchemaInfo)
  );

  return (
    <Modal
      title={props.disabled ? 'View mapping' : 'Configure mapping'}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(
          mappingsHaveChanged
            ? generateSchemaMapperConfig(props.toSchemaInfo, fieldToFieldMapperData)
            : undefined
        );
      }}
      buttons={
        mappingsHaveChanged
          ? [
              {
                key: 'finish',
                text: 'Finish',
                disabled: disabledMessage != null,
                submit: true,
              },
            ]
          : [
              {
                key: 'close',
                text: 'Close',
                submit: true,
                style: ButtonStyle.TERTIARY_GRAY,
              },
            ]
      }
      expandHeight={true}
      large={true}
    >
      <SchemaMapperConfigModalBody
        flowchartTaskSchema={props.flowchartTaskSchema}
        disabled={props.disabled}
        fromSchemaInfos={fromSchemaInfos}
        toSchemaInfo={props.toSchemaInfo}
        onChangeFromItemType={onChangeFromItemType}
        onChangeFieldMapperSource={onChangeFieldMapperSource}
        fieldToFieldMapperData={fieldToFieldMapperData}
        removeLookupConfigForField={removeLookupConfigForField}
        onSubmit={onSubmitLookupConfig}
        onChangeResultMustBeNonNullToMap={onChangeResultMustBeNonNullToMap}
        allSchemasById={props.allSchemasById}
        errorMessage={disabledMessage}
      />
    </Modal>
  );
}
